<template>
  <div
    class="d-flex-column blog_content_parent justify-start align-start gap-0 t-white"
  >
    <div class="blog_content">
      <PortableText
        :value="blog?.content"
        :components="portableTextComponents"
        v-if="blog?.content"
      />
    </div>
  </div>
</template>

<script setup>
import { h } from "vue";
import { PortableText } from "@portabletext/vue";
import { toast } from "vue-sonner";
import AnchorIcon from "../icons/AnchorIcon.vue";
import CopyIcon from "../icons/CopyIcon.vue";
const { public: publicConfig } = useRuntimeConfig();
const props = defineProps({
  blog: {
    type: Object,
    default: null,
  },
});
// Generate a slug from text for anchor links
const generateSlug = (text) => {
  return text
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^\w-]+/g, "");
};

const copyUrl = (url) => {
  // console.log(url);
  if (!url) return;
  navigator.clipboard
    .writeText(url)
    .then(() => {
      toast("URL copied to your clipboard", {
        style: {
          background: "#08090a",
          borderColor: "#23252a",
          color: "#f7f8f8",
        },
        icon: AnchorIcon,
      });

      // alert("Code copied to clipboard!"); // Optional: Replace with a toast notification
    })
    .catch((err) => {
      console.error("Failed to copy code: ", err);
    });
};

// Custom components for PortableText
const portableTextComponents = {
  types: {
    codeBlock: ({ value }) => {
      const copyCodeToClipboard = () => {
        if (!value?.code) return;
        navigator.clipboard
          .writeText(value.code)
          .then(() => {
            toast("Code has been copied to your clipboard", {
              style: {
                background: "#08090a",
                borderColor: "#23252a",
                color: "#f7f8f8",
              },
              icon: CopyIcon,
            });

            // alert("Code copied to clipboard!"); // Optional: Replace with a toast notification
          })
          .catch((err) => {
            console.error("Failed to copy code: ", err);
          });
      };

      return h("pre", { class: "code-block-container" }, [
        h(
          "div",
          {
            class:
              "cursor-pointer copy_code_container font-inter t-grey regular-s",
            onClick: copyCodeToClipboard,
          },
          "Copy Code"
        ),
        h("div", { class: "code_container" }, h("code", value?.code)),
      ]);
    },

    table: ({ value }) => {
      if (!value?.rows) return null;
      return h(
        "table",
        { class: "border-collapse border border-gray-400 w-full my-4" },
        [
          h(
            "tbody",
            value.rows.map((row) =>
              h(
                "tr",
                { class: "border border-gray-400" },
                row.cells.map((cell) =>
                  h("td", { class: "border border-gray-400 p-2" }, cell)
                )
              )
            )
          ),
        ]
      );
    },

    embedVideo: ({ value }) =>
      h("iframe", {
        src: value?.url,
        width: "100%",
        height: "315",
        frameborder: "0",
        allow: "autoplay; encrypted-media",
        allowfullscreen: true,
      }),

    image: ({ value }) => {
      const imageElement = h("img", {
        src: value?.asset?.url, // Ensure the image has a valid URL
        alt: value?.alt || "",
      });

      if (value?.link) {
        return h(
          "a",
          {
            href: value.link, // Optional link
            target: "_blank", // Open in a new tab
            rel: "noopener noreferrer", // Security best practice
          },
          [imageElement]
        );
      }

      return imageElement; // If no link, just return the image
    },

    callout: ({ value }) => {
      return h(
        "div",
        {
          class: `callout`,
          style: {
            backgroundColor: value.backgroundColor,
            color: value.textColor,
            borderLeft: `1px solid ${value.textColor}`,
          },
        },
        value?.text
      );
    },
  },

  block: {
    h2: ({ value }) => {
      if (!value.children || !Array.isArray(value.children)) return null;
      const text = value.children.map((child) => child.text).join(" ");
      const id = generateSlug(text);

      return h("h2", { id, class: "h2-group" }, [
        h(
          resolveComponent("NuxtLink"),
          {
            to: `?navto=${id}`,
            id: `link-${id}`,
            class: "d-flex-row justify-start height-max-content",
            onClick: () => {
              const path = `${publicConfig.appUrl}/blog/${props.blog?.slug?.current}?navto=${id}`;
              copyUrl(path);
            },
          },
          [text, h(AnchorIcon, {})]
        ),
      ]);
    },
    h3: ({ value }) => {
      if (!value.children || !Array.isArray(value.children)) return null;
      const text = value.children.map((child) => child.text).join(" ");
      const id = generateSlug(text);
      return h("h3", { id, class: "h2-group" }, [
        h(
          resolveComponent("NuxtLink"),
          {
            to: `?navto=${id}`,
            id: `link-${id}`,
            class: "d-flex-row justify-start height-max-content",
            onClick: () => {
              const path = `${publicConfig.appUrl}/blog/${props.blog?.slug?.current}?navto=${id}`;
              copyUrl(path);
            },
          },
          [text, h(AnchorIcon, {})]
        ),
      ]);
    },
  },
};
</script>
